import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth, type CurrentUser } from '../../auth/AuthContext';
import { CredentialsForm } from './CredentialsForm';
import { OtpForm } from './OtpForm';
import { EnrollmentForm } from './EnrollmentForm';
import { RecoveryCodesScreen } from './RecoveryCodesScreen';

type Step =
  | { name: 'credentials' }
  | { name: 'otp'; challengeToken: string }
  | { name: 'enroll'; challengeToken: string }
  | { name: 'recovery'; codes: string[]; user: CurrentUser };

/** Password, then the second factor — always: enrollment the first time, a
 *  code every time after. The temporary password is replaced right after. */
export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>({ name: 'credentials' });

  function enter(user: CurrentUser) {
    navigate(user.allow_password_change ? '/change-password' : '/', { replace: true });
  }

  async function onCredentials(email: string, password: string) {
    const result = await login(email, password);
    setStep(
      result.kind === 'otp_required'
        ? { name: 'otp', challengeToken: result.challengeToken }
        : { name: 'enroll', challengeToken: result.challengeToken },
    );
  }

  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      {step.name === 'credentials' && <CredentialsForm onSubmit={onCredentials} />}
      {step.name === 'otp' && <OtpForm challengeToken={step.challengeToken} onSuccess={enter} />}
      {step.name === 'enroll' && (
        <EnrollmentForm
          challengeToken={step.challengeToken}
          onEnrolled={(codes, user) => setStep({ name: 'recovery', codes, user })}
        />
      )}
      {step.name === 'recovery' && <RecoveryCodesScreen codes={step.codes} onDone={() => enter(step.user)} />}
    </main>
  );
}
